<script setup lang="ts">
import { computed } from 'vue'
import ProgressSpinner from 'primevue/progressspinner'
import ResourceCard from '@/resources/ResourceCard.vue'
import ResourceTile from '@/resources/ResourceTile.vue'
import MultilingualResourcePreviewCards from '@/resources/MultilingualResourcePreviewCards.vue'
import { filterResourcesByLanguage, hasNonEnglishLanguage, sortBySetNumber } from '@/resources/resource_list'
import type { AnalyticsData } from '@/tracking/analytics/analyticsTracker'
import type { Resource } from '@/resources/resource_types'

const props = withDefaults(
  defineProps<{
    resources: Resource[]
    isFetchingResources?: boolean
    selectedLanguage?: string
    isTiled?: boolean
    isIndented?: boolean
    evtData?: AnalyticsData
    headingLevel?: number
  }>(),
  {
    isFetchingResources: false,
    selectedLanguage: '',
    isTiled: false,
    isIndented: false,
    evtData: () => ({}),
    headingLevel: 4
  }
)

const hasFetchedResources = computed(() => !props.isFetchingResources && props.resources.length > 0)
const isMultilingual = computed(() => hasFetchedResources.value && hasNonEnglishLanguage(props.resources))
const resourcesInSelectedLanguage = computed(() => filterResourcesByLanguage(props.resources, props.selectedLanguage))
const resourcesInSetOrder = computed(() => sortBySetNumber(resourcesInSelectedLanguage.value))
const containerClass = computed(() => ['flex flex-wrap text-md', { 'ml-8': props.isIndented }])
</script>

<template>
  <!-- Slotted content (generated assessments) carries no language of its own, so it renders
    under every language selection, including one that filters the curated list to nothing. -->
  <div :class="containerClass" :role="isFetchingResources || isMultilingual ? undefined : 'list'" data-testid="resource-list">
    <MultilingualResourcePreviewCards
      v-if="isMultilingual"
      :resources="resourcesInSetOrder"
      :selected-language="selectedLanguage"
      :is-tiled="isTiled"
      :evt-data="evtData"
      :heading-level="headingLevel"
    >
      <slot />
    </MultilingualResourcePreviewCards>
    <template v-else>
      <p v-if="isFetchingResources" class="flex items-center text-gray-700" data-testid="resource-list-loading">
        <ProgressSpinner stroke-width="8" class="!ml-0 !mr-2 h-4 w-4 stroke-gray-700" aria-hidden="true" />
        Loading Resources...
      </p>
      <template v-else-if="isTiled">
        <ResourceTile v-for="resource in resourcesInSetOrder" :key="resource.uuid" role="listitem" :resource="resource" :evt-data="evtData" />
      </template>
      <template v-else>
        <ResourceCard
          v-for="resource in resourcesInSetOrder"
          :key="resource.uuid"
          v-bind="resource"
          role="listitem"
          :heading-level="headingLevel"
        />
      </template>
      <slot />
    </template>
  </div>
</template>
